import classNames from "classnames";
import ImageCell from "./ImageCell";
import { useLocSettings } from "../../hooks/SettingsHooks";
import { useSiteFeatures } from "../../hooks/useSiteFeatures";
import { Product } from "../../types/Product";
import GridCell, { GridCellCommonProps } from "./GridCell";
import { Asset } from "../../types/Asset";
import { useProductWithCockpitId } from "../../hooks/ProductHooks";
import { Suspense } from "react";
import { Spinner } from "reactstrap";
import "./style.scss";
import { useProductUrl } from "../../utils/ProductUtils";

const Icons = ({ prod }: { prod: Product }) => {
  const settings = useLocSettings();

  const icons = Object.entries(prod.specsIndexed || {})
    .map(([field, { value, unit }]) => {
      //TODO: const show_icon = settings.specifications?.[field]?.show_icon;
      const icon = settings.specifications?.[field]?.icons?.[value];
      return { field, value, unit, icon, sku: prod.sku };
    })
    .filter(icon => !!icon);

  return (
    <span className="spec-icons">
      {icons
        .filter(({ field }) =>
          [
            "Type of wood",
            "Finish",
            "Pattern",
            "Grading",
            "Water resistance"
          ].includes(field)
        )
        .map(({ field, value }) => {
          const icon = settings.specifications?.[field]?.icons?.[value];

          return icon ? (
            <span
              key={field}
              className="spec-icon"
              style={{
                color: "inherit"
              }}
              dangerouslySetInnerHTML={{
                __html:
                  icon.svg2?.replaceAll(
                    /fill="#[0-9a-fA-F]{3,6}"/g,
                    'fill="currentColor"'
                  ) || icon.svg
              }}
            />
          ) : null;
        })}
    </span>
  );
};

const ProductCellInner = ({
  id,
  className,
  cellSize = "1x1",
  product: _product,
  theme = "black",
  requiresFeature,
  ...unhandledProps
}: GridCellCommonProps & { product: Product }) => {
  const product = useProductWithCockpitId(_product?._id);
  const productUrl = useProductUrl(product);

  return product ? (
    <ImageCell
      id={id}
      className={classNames("product-cell", className)}
      cellSize={cellSize}
      theme={theme}
      requiresFeature={requiresFeature}
      heading={product.name}
      image={product.images?.[0] as Asset}
      url={productUrl}
      {...unhandledProps}
    >
      <Icons prod={product} />
    </ImageCell>
  ) : null;
};

const ProductCell = ({
  id,
  className,
  cellSize = "1x1",
  product: _product,
  theme = "black",
  requiresFeature,
  ...unhandledProps
}: GridCellCommonProps & { product: Product }) => {
  const features = useSiteFeatures();

  return !requiresFeature || features.hasFeature(requiresFeature) ? (
    <Suspense
      fallback={
        <GridCell
          id={id}
          className={classNames("product-cell", className)}
          cellSize={cellSize}
          theme={theme}
        >
          <Spinner />
        </GridCell>
      }
    >
      <ProductCellInner
        id={id}
        className={classNames("product-cell", className)}
        cellSize={cellSize}
        theme={theme}
        product={_product}
        requiresFeature={requiresFeature}
        {...unhandledProps}
      />
    </Suspense>
  ) : null;
};

export default ProductCell;
